import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import { Button, ButtonGroup, Modal, Panel, PanelGroup, Text, designSystem } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import DeviceModeDropdown from './device-mode-dropdown';
import {
  APPEARANCE_TAB_ID,
  DIALOG_HEIGHT,
  DIALOG_WIDTH,
  DIALOG_MIN_WIDTH,
  DIALOG_MIN_HEIGHT,
  DIALOG_PANEL_HEADER_HEIGHT,
  DIALOG_PANEL_FOOTER_HEIGHT,
} from '../constants/common';

const {
  Tabs,
  TabsList,
  Tab,
  TabsContentList,
  TabContent,
  Accordion,
  AccordionContent,
  AccordionGroup,
  AccordionHeader,
} = designSystem;

function ElementSettingsDialog({
  modalRef,
  captions,
  tabs,
  tabsActiveId,
  tabsScrollContainerRef,
  deviceModeOptions = [],
  activeDeviceMode = null,
  offsetX,
  offsetY,
  visuallyHidden = false,
  accordionDefaultActiveItems = null,
  isDeviceModeSwitcherVisible = false,
  onAccordionToggle,
  onSubmit,
  onCancel,
  onOverlayClick,
  onTabChange,
  onDragEnd,
  onReady,
  onDeviceModeChange,
  onDeviceModeSelectClick,
}) {
  const isFullscreen = bowser.mobile;
  const hasAppearanceTab = tabs.appearance.length > 0;

  const modalClassName = classNames('ws-element-settings', {
    'ws-element-settings--visually-hidden': visuallyHidden,
  });

  const handleSubmit = useCallback(() => onSubmit({ shouldScrollToTarget: true }), [onSubmit]);
  const handleCancel = useCallback(() => onCancel({ shouldScrollToTarget: true }), [onCancel]);

  return (
    <Modal
      modalRef={modalRef}
      className={modalClassName}
      dragHandleSelector=".ws-drag-trigger"
      floatingModalElementSelector=".ws-context-menu, .ws-ui-action-list-item"
      width={DIALOG_WIDTH}
      height={DIALOG_HEIGHT}
      minWidth={DIALOG_MIN_WIDTH}
      minHeight={DIALOG_MIN_HEIGHT}
      offsetX={offsetX}
      offsetY={offsetY}
      overlay="none"
      resizable={false}
      fullscreen={isFullscreen}
      draggable
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      onDragEnd={onDragEnd}
      onReady={onReady}
      onOverlayClick={onOverlayClick}
    >
      <PanelGroup height="100%">
        <Panel
          height={DIALOG_PANEL_HEADER_HEIGHT}
          theme="gray-100"
          corners="squared"
          align="middle"
          className="ws-drag-trigger ws-element-settings__title"
        >
          <Text type="heading-5">{captions.title}</Text>
        </Panel>
        <Panel
          className="ws-element-settings__main"
          height={`calc(100% - ${DIALOG_PANEL_FOOTER_HEIGHT + DIALOG_PANEL_HEADER_HEIGHT}px)`}
          scrollable
        >
          <Tabs
            activeTabId={tabsActiveId}
            appearance="medium"
            onTabChange={onTabChange}
            disableSwipeableContent
            noSideSpacing
            noVerticalSpacing
            scrollContainerRef={tabsScrollContainerRef}
          >
            <div className="ws-element-settings__tabs-header">
              <TabsList variant="scrollable">
                {tabs.custom.map((tab) => (
                  <Tab key={tab.id} tabId={tab.id} label={tab.title} />
                ))}
                {hasAppearanceTab && (
                  <Tab tabId={APPEARANCE_TAB_ID} label={i18next.t('Appearance')} />
                )}
              </TabsList>

              {isDeviceModeSwitcherVisible && (
                <div className="ws-element-settings__device-mode">
                  <DeviceModeDropdown
                    modeOptions={deviceModeOptions}
                    activeMode={activeDeviceMode}
                    onChange={onDeviceModeChange}
                    onShow={onDeviceModeSelectClick}
                  />
                </div>
              )}
            </div>

            <TabsContentList>
              {tabs.custom.map((tab) => (
                <TabContent key={tab.id} tabId={tab.id}>
                  {tab.component}
                </TabContent>
              ))}

              {hasAppearanceTab && (
                <TabContent tabId={APPEARANCE_TAB_ID}>
                  <Accordion
                    type="all"
                    appearance="grey"
                    defaultActiveItem={accordionDefaultActiveItems}
                    onToggleItem={onAccordionToggle}
                  >
                    {tabs.appearance.map((appearanceTab) => (
                      <AccordionGroup key={appearanceTab.id} itemId={appearanceTab.id}>
                        <AccordionHeader
                          isSticky
                          title={appearanceTab.accordionTitle.toUpperCase()}
                        />
                        <AccordionContent>{appearanceTab.component}</AccordionContent>
                      </AccordionGroup>
                    ))}
                  </Accordion>
                </TabContent>
              )}
            </TabsContentList>
          </Tabs>
        </Panel>
        <Panel
          height={DIALOG_PANEL_FOOTER_HEIGHT}
          corners="squared"
          className="ws-block-layout-settings__footer"
        >
          <ButtonGroup stick="top" block>
            <Button stretch="block" size="large" onClick={handleSubmit}>
              {captions.buttons.submit}
            </Button>
            <Button stretch="block" size="large" onMouseDown={onCancel}>
              {captions.buttons.cancel}
            </Button>
          </ButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

ElementSettingsDialog.propTypes = {
  modalRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  captions: PropTypes.shape().isRequired,
  tabs: PropTypes.shape({
    appearance: PropTypes.array.isRequired,
    custom: PropTypes.array.isRequired,
  }).isRequired,
  tabsActiveId: PropTypes.string.isRequired,
  tabsScrollContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  deviceModeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      mode: PropTypes.string.isRequired,
      glyph: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  isDeviceModeSwitcherVisible: PropTypes.bool,
  activeDeviceMode: PropTypes.string,
  visuallyHidden: PropTypes.bool,
  accordionDefaultActiveItems: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.string,
    PropTypes.oneOf([null]),
  ]),
  onAccordionToggle: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOverlayClick: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onReady: PropTypes.func.isRequired,
  onDeviceModeChange: PropTypes.func.isRequired,
  onDeviceModeSelectClick: PropTypes.func.isRequired,
};

export default ElementSettingsDialog;
